import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import axios, { baseUrl } from "../../axios/axios";

// Create the context
const GlobalContext = createContext<any>({});

// Helper function to build filter specification string
const buildFilterString = (
  statusFilter: string,
  therapyTaken?: "all" | "taken" | "pending"
): string | undefined => {
  const filters = [];

  // Add status filter if present
  if (statusFilter) {
    filters.push(`status in (${statusFilter})`);
  }

  // Add therapyTaken filter if specified and not 'all'
  if (therapyTaken && therapyTaken !== "all") {
    const taken = therapyTaken === "taken";
    filters.push(`therapyTaken:${taken}`);
  }

  return filters.length > 0 ? filters.join(" and ") : undefined;
};

// Create the provider component
export const GlobalProvider = ({ children }: any) => {
  const [remindersList, setRemindersList] = useState([]);
  const [remindersFilter, setRemindersFilter] = useState<any>({});

  // Require marketplace IDs as parameters instead of reading localStorage
  const fetchUserReminders = useCallback(
    (
      statuses: any = "",
      optionalFilters: any = null,
      marketplaceIds?: {
        branchMarketplaceId: string | number;
        marketplaceId: string | number;
      }
    ) => {
      // Get marketplace IDs from parameters (passed by calling component)
      let branchMarketplaceId: string | number | null;
      let marketplaceId: string | number | null;

      if (marketplaceIds) {
        // Use provided IDs
        branchMarketplaceId = marketplaceIds.branchMarketplaceId;
        marketplaceId = marketplaceIds.marketplaceId;
      } else {
        // Fallback to localStorage (but warn about potential race condition)
        branchMarketplaceId = localStorage.getItem("branchMarketplaceId");
        marketplaceId = localStorage.getItem("marketplaceId");
        console.warn(
          "GlobalContext: Reading localStorage for marketplace IDs - consider passing as parameters to avoid race conditions"
        );
      }

      // Guard against null/undefined values
      if (!branchMarketplaceId || !marketplaceId) {
        console.warn(
          "GlobalContext: Cannot fetch reminders - marketplace IDs not available"
        );
        return Promise.reject("Marketplace IDs not available");
      }

      // Use provided filters or fallback to global state
      const filters = optionalFilters || remindersFilter || {};
      // Extract therapyTaken from filters
      const { createdFrom, createdTo, groups, sort, therapyTaken } = filters;

      const filterStatus = (statuses || "PENDING,WAITING,ORDERED,EXPIRED")
        .split(",")
        .filter((i: any) => i)
        .map((i: any) => `'${i}'`)
        .join(",");

      console.log("🔍 DEBUG: therapyTaken value:", therapyTaken);
      console.log(
        "🔍 DEBUG: Filter string:",
        buildFilterString(filterStatus, therapyTaken)
      );

      const params: any = {
        branchMarketplaceId: branchMarketplaceId,
        marketplaceId: marketplaceId,
        from: createdFrom,
        to: createdTo,
        groupIds: groups,
        page: 0,
        numOfReturns: 1000,
        // Use buildFilterString to include therapyTaken
        filter: buildFilterString(filterStatus, therapyTaken),
      };

      console.log("🔍 DEBUG: Final params:", params);

      // Add sort parameter if provided
      if (sort && Array.isArray(sort) && sort.length > 0) {
        params.sort = sort.map((s: any) => `${s.field}:${s.direction}`);
      }

      console.log("🟢 GlobalContext: Final API params:", params);

      return axios.get(baseUrl + `/rfzo_therapy_reminder`, {
        params,
        paramsSerializer: {
          indexes: null, // Serialize arrays as sort=value1&sort=value2
        },
      });
    },
    [remindersFilter]
  );

  // Admin version also gets therapyTaken support
  const fetchUserRemindersAsAdmin = useCallback(
    (statuses: any = "", optionalFilters: any = null) => {
      // Use provided filters or fallback to global state
      const filters = optionalFilters || remindersFilter || {};
      // Extract therapyTaken from filters
      const { createdFrom, createdTo, groups, sort, therapyTaken } = filters;

      const filterStatus = statuses
        ? statuses
            ?.split(",")
            .map((i: any) => `'${i}'`)
            .join(",")
        : "";

      console.log("🔍 DEBUG ADMIN: therapyTaken value:", therapyTaken);
      console.log(
        "🔍 DEBUG ADMIN: Filter string:",
        buildFilterString(
          filterStatus ? `status in (${filterStatus})` : "",
          therapyTaken
        )
      );

      const params: any = {
        from: createdFrom,
        to: createdTo,
        groupIds: groups,
        page: 0,
        numOfReturns: 1000,
        // Use buildFilterString to include therapyTaken
        filter: buildFilterString(
          filterStatus ? `status in (${filterStatus})` : "",
          therapyTaken
        ),
      };

      console.log("🔍 DEBUG ADMIN: Final params:", params);

      // Add sort parameter if provided
      if (sort && Array.isArray(sort) && sort.length > 0) {
        params.sort = sort.map((s: any) => `${s.field}:${s.direction}`);
      }

      console.log("🟢 GlobalContext: Final admin API params:", params);

      return axios.get(baseUrl + `/rfzo_therapy_reminder`, {
        params,
        paramsSerializer: {
          indexes: null, // Serialize arrays as sort=value1&sort=value2
        },
      });
    },
    [remindersFilter]
  );

  // Updated fetchWrap to handle marketplace IDs properly
  const fetchWrap = useCallback(
    (
      statuses: any = "",
      isAdmin: any,
      optionalFilters: any = null,
      marketplaceIds?: {
        branchMarketplaceId: string | number;
        marketplaceId: string | number;
      }
    ) => {
      if (isAdmin) {
        return fetchUserRemindersAsAdmin(statuses, optionalFilters);
      } else {
        return fetchUserReminders(statuses, optionalFilters, marketplaceIds);
      }
    },
    [fetchUserReminders, fetchUserRemindersAsAdmin]
  );

  // Enhanced clear function
  const clearContext = useCallback(() => {
    setRemindersList([]);
    setRemindersFilter({});

    // Additional cleanup if needed
    console.log("GlobalContext: Context cleared successfully");
  }, []);

  const contextValue = useMemo(
    () => ({
      fetchWrap,
      fetchUserReminders,
      fetchUserRemindersAsAdmin,
      remindersList,
      setRemindersList,
      remindersFilter,
      setRemindersFilter,
      clearContext,
    }),
    [
      fetchWrap,
      fetchUserReminders,
      fetchUserRemindersAsAdmin,
      remindersList,
      remindersFilter,
      clearContext,
    ]
  );

  return (
    <GlobalContext.Provider value={contextValue}>
      {children}
    </GlobalContext.Provider>
  );
};

// Hook to use the global context
export const useGlobalContext = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error("useGlobalContext must be used within a GlobalProvider");
  }
  return context;
};
